/**
 * Thumbnail utility functions for profile images and company logos
 * Implements LinkedIn-style image processing and sizing
 */

export interface ThumbnailOptions {
  width: number;
  height: number;
  quality?: number;
  format?: 'jpeg' | 'png' | 'webp';
  cropMode?: 'cover' | 'contain' | 'fill';
}

export interface ProfileThumbnails {
  userPhoto?: string;
  companyLogo?: string;
}

/**
 * LinkedIn-style thumbnail sizes
 */
export const THUMBNAIL_SIZES = {
  // User profile photos (LinkedIn style: 400x400)
  USER_PHOTO: {
    width: 400,
    height: 400,
    quality: 0.9,
    format: 'jpeg' as const,
    cropMode: 'cover' as const
  },
  
  // Company logos (LinkedIn style: 300x300 for square, 400x200 for wide)
  COMPANY_LOGO: {
    width: 300,
    height: 300,
    quality: 0.9,
    format: 'png' as const,
    cropMode: 'contain' as const
  },
  
  // Small thumbnails for project lists
  SMALL_THUMBNAIL: {
    width: 64,
    height: 64,
    quality: 0.8,
    format: 'jpeg' as const,
    cropMode: 'cover' as const
  },
  
  // Medium thumbnails for project details
  MEDIUM_THUMBNAIL: {
    width: 120,
    height: 120,
    quality: 0.9,
    format: 'jpeg' as const,
    cropMode: 'cover' as const
  }
};

/**
 * Create a thumbnail with LinkedIn-style processing
 * @param file - The image file to process
 * @param options - Thumbnail options
 * @returns Promise<string> - Base64 data URL of the processed thumbnail
 */
export const createThumbnail = (file: File, options: ThumbnailOptions): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    img.onload = () => {
      // Set canvas dimensions
      canvas.width = options.width;
      canvas.height = options.height;

      // Calculate dimensions based on crop mode
      let sourceX = 0, sourceY = 0, sourceWidth = img.width, sourceHeight = img.height;
      let destX = 0, destY = 0, destWidth = options.width, destHeight = options.height;

      const imgAspectRatio = img.width / img.height;
      const targetAspectRatio = options.width / options.height;

      switch (options.cropMode) {
        case 'cover':
          // LinkedIn-style: crop to fill the entire area
          if (imgAspectRatio > targetAspectRatio) {
            // Image is wider - crop horizontally
            sourceWidth = img.height * targetAspectRatio;
            sourceX = (img.width - sourceWidth) / 2;
          } else {
            // Image is taller - crop vertically
            sourceHeight = img.width / targetAspectRatio;
            sourceY = (img.height - sourceHeight) / 2;
          }
          break;

        case 'contain':
          // Fit entire image with padding if needed
          if (imgAspectRatio > targetAspectRatio) {
            // Image is wider - fit by width
            destHeight = options.width / imgAspectRatio;
            destY = (options.height - destHeight) / 2;
          } else {
            // Image is taller - fit by height
            destWidth = options.height * imgAspectRatio;
            destX = (options.width - destWidth) / 2;
          }
          
          // Fill background with white for logos
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, options.width, options.height);
          break;

        case 'fill':
          // Stretch to fill (not recommended for profiles)
          break;
      }

      // Apply subtle blur for better quality at small sizes
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw the image
      ctx.drawImage(
        img,
        sourceX, sourceY, sourceWidth, sourceHeight,
        destX, destY, destWidth, destHeight
      );

      // Convert to data URL
      const quality = options.quality || 0.9;
      const mimeType = `image/${options.format || 'jpeg'}`;
      
      const dataUrl = canvas.toDataURL(mimeType, quality);
      resolve(dataUrl);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Load the image
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    // Clean up
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      
      // Re-execute the processing logic
      canvas.width = options.width;
      canvas.height = options.height;

      let sourceX = 0, sourceY = 0, sourceWidth = img.width, sourceHeight = img.height;
      let destX = 0, destY = 0, destWidth = options.width, destHeight = options.height;

      const imgAspectRatio = img.width / img.height;
      const targetAspectRatio = options.width / options.height;

      switch (options.cropMode) {
        case 'cover':
          if (imgAspectRatio > targetAspectRatio) {
            sourceWidth = img.height * targetAspectRatio;
            sourceX = (img.width - sourceWidth) / 2;
          } else {
            sourceHeight = img.width / targetAspectRatio;
            sourceY = (img.height - sourceHeight) / 2;
          }
          break;

        case 'contain':
          if (imgAspectRatio > targetAspectRatio) {
            destHeight = options.width / imgAspectRatio;
            destY = (options.height - destHeight) / 2;
          } else {
            destWidth = options.height * imgAspectRatio;
            destX = (options.width - destWidth) / 2;
          }
          
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, options.width, options.height);
          break;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(
        img,
        sourceX, sourceY, sourceWidth, sourceHeight,
        destX, destY, destWidth, destHeight
      );

      const quality = options.quality || 0.9;
      const mimeType = `image/${options.format || 'jpeg'}`;
      
      const dataUrl = canvas.toDataURL(mimeType, quality);
      resolve(dataUrl);
    };
  });
};

/**
 * Create user profile photo thumbnail (LinkedIn style)
 * @param file - The image file
 * @returns Promise<string> - Processed thumbnail data URL
 */
export const createUserPhotoThumbnail = (file: File): Promise<string> => {
  return createThumbnail(file, THUMBNAIL_SIZES.USER_PHOTO);
};

/**
 * Create company logo thumbnail (LinkedIn style)
 * @param file - The image file
 * @returns Promise<string> - Processed thumbnail data URL
 */
export const createCompanyLogoThumbnail = (file: File): Promise<string> => {
  return createThumbnail(file, THUMBNAIL_SIZES.COMPANY_LOGO);
};

/**
 * Create small thumbnail for project lists
 * @param file - The image file
 * @returns Promise<string> - Processed thumbnail data URL
 */
export const createSmallThumbnail = (file: File): Promise<string> => {
  return createThumbnail(file, THUMBNAIL_SIZES.SMALL_THUMBNAIL);
};

/**
 * Create medium thumbnail for project details
 * @param file - The image file
 * @returns Promise<string> - Processed thumbnail data URL
 */
export const createMediumThumbnail = (file: File): Promise<string> => {
  return createThumbnail(file, THUMBNAIL_SIZES.MEDIUM_THUMBNAIL);
};

/**
 * Validate image file for thumbnails
 * @param file - The file to validate
 * @param maxSizeMB - Maximum file size in MB (default: 10MB)
 * @returns boolean - True if file is valid
 */
export const validateThumbnailFile = (file: File, maxSizeMB: number = 10): boolean => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  const maxSize = maxSizeMB * 1024 * 1024;

  if (!validTypes.includes(file.type)) {
    return false;
  }

  if (file.size > maxSize) {
    return false;
  }

  return true;
};

/**
 * Get optimal thumbnail size based on usage context
 * @param context - Where the thumbnail will be used
 * @returns ThumbnailOptions - Optimal size configuration
 */
export const getThumbnailSizeForContext = (context: 'profile' | 'project-list' | 'project-detail' | 'company-logo'): ThumbnailOptions => {
  switch (context) {
    case 'profile':
      return THUMBNAIL_SIZES.USER_PHOTO;
    case 'project-list':
      return THUMBNAIL_SIZES.SMALL_THUMBNAIL;
    case 'project-detail':
      return THUMBNAIL_SIZES.MEDIUM_THUMBNAIL;
    case 'company-logo':
      return THUMBNAIL_SIZES.COMPANY_LOGO;
    default:
      return THUMBNAIL_SIZES.SMALL_THUMBNAIL;
  }
};

/**
 * Create multiple thumbnail sizes from a single image
 * @param file - The image file
 * @param contexts - Array of contexts to create thumbnails for
 * @returns Promise<Record<string, string>> - Object with context keys and thumbnail data URLs
 */
export const createMultipleThumbnails = async (
  file: File, 
  contexts: Array<'profile' | 'project-list' | 'project-detail' | 'company-logo'>
): Promise<Record<string, string>> => {
  const thumbnails: Record<string, string> = {};
  
  for (const context of contexts) {
    const options = getThumbnailSizeForContext(context);
    thumbnails[context] = await createThumbnail(file, options);
  }
  
  return thumbnails;
};

/**
 * Generate initials from name for fallback avatars
 * @param name - Full name or username
 * @returns string - Initials (max 2 characters)
 */
export const generateInitials = (name: string): string => {
  if (!name) return 'U';
  
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generate a color for initials avatar based on name
 * @param name - Name to generate color from
 * @returns string - CSS color value
 */
export const generateAvatarColor = (name: string): string => {
  const colors = [
    '#1f2937', '#374151', '#4b5563', '#6b7280',
    '#dc2626', '#ea580c', '#d97706', '#ca8a04',
    '#65a30d', '#16a34a', '#059669', '#0d9488',
    '#0891b2', '#0284c7', '#2563eb', '#4f46e5',
    '#7c3aed', '#a21caf', '#be185d', '#e11d48'
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};